// ComingSoonPage — CHG-2572 / ITER-905309 (DRIFT-960). Generic "Coming
// soon" stub for non-platform forwardPromised nav surfaces whose
// nav.ts entry declares a route that does not have a dedicated page yet.
//
// Background: forwardPromised nav items render as non-clickable SOON
// rows in the sidebar (Sidebar.tsx, CHG-1473) and as a defer-promise
// notice in the command palette (usePalette.ts), so the row itself
// cannot navigate into a 404. But the declared route (e.g. "/signage")
// still has no App.tsx route, so a DIRECT URL / deep link / bookmark
// fell through to the generic NotFound catch-all — a styled 404 panel,
// not the "coming soon" affordance the SOON badge promises (DRIFT-960).
//
// This is the non-platform analog of PlatformStubPage (CHG-1551): same
// "reserve the route so deep links don't 404, swap the element prop
// when the real page lands" pattern, minus the platform-admin copy and
// the back-to-/platform link. The destination of the back link is
// caller-supplied (defaults to /overview, the standard v2 landing).

import { Link } from "react-router-dom";
import { DashboardPage } from "../components/composition/DashboardPage";

export interface ComingSoonPageProps {
  /** Surface title, e.g. "Signage". */
  title: string;
  /** Optional subtitle override. Defaults to generic "coming soon" copy
   *  that names the surface. */
  subtitle?: string;
  /** Where the recovery link points. Defaults to the v2 landing. */
  backTo?: string;
  /** Label for the recovery link. Defaults to "← Back to Overview". */
  backLabel?: string;
}

export function ComingSoonPage({
  title,
  subtitle,
  backTo = "/overview",
  backLabel = "← Back to Overview",
}: ComingSoonPageProps) {
  const resolvedSubtitle =
    subtitle ??
    `The ${title.toLowerCase()} surface is coming soon. The route is reserved so deep links won't 404; the real page will land in a follow-up CHG.`;

  return (
    <DashboardPage
      title={title}
      subtitle={resolvedSubtitle}
      stats={
        <div
          data-testid="coming-soon-banner"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 12px",
            borderRadius: 9999,
            background: "var(--color-bg-subtle)",
            color: "var(--color-text-muted)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          Coming soon
        </div>
      }
      feed={
        <section
          className="card"
          aria-labelledby="coming-soon-back-title"
          style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}
        >
          <h2
            id="coming-soon-back-title"
            style={{ fontSize: 14, fontWeight: 500, margin: 0 }}
          >
            What's here today
          </h2>
          <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>
            The {title.toLowerCase()} surface isn't built yet in this dashboard.
            The nav keeps it visible so it's discoverable; the page will follow.
          </p>
          <Link
            to={backTo}
            data-testid="coming-soon-back-link"
            style={{
              fontSize: 13,
              color: "var(--color-text)",
              textDecoration: "underline",
            }}
          >
            {backLabel}
          </Link>
        </section>
      }
    />
  );
}
